import { Info as TabIconInfo, FileJson as TabIconFileJson, Network as TabIconNetwork, Percent as TabIconPercent, Gauge as TabIconGauge, Settings2 as TabIconSettings2 } from "lucide-react";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { Badge } from "@ui/badge/Badge";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Checkbox } from "@ui/checkbox/Checkbox";
import { Error } from "@ui/error/Error";
import { IconArrowDown } from "@ui/icons/IconArrowDown";
import { IconCalendar } from "@ui/icons/IconCalendar";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { IconVisa } from "@ui/icons/IconVisa";
import { IconVisaAndMaster } from "@ui/icons/IconVisaAndMaster";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { TitledValue } from "@ui/table/TitledValue";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Typo } from "@ui/text/Typo";
import Signal from "badmfck-signal";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { useRouter } from "src/services/Router";
import { ITerminal } from "src/types/types";
import { GBox, HeadBlock } from "../conponents/GBox";
import { TerminalBadge } from "../conponents/TerminalBadge";
import { FormParser } from "../FormParser";
import { Limits } from "../Limits";
import { TerminalsBlock } from "./ClientCredentialsPopup";
import { ContentPopup } from "./general/ContentPopup";

export const S_ACQUIRER_POPUP = new Signal<{ id: number; channelId?: number } | null>();

export const AcquirerPopup = () => {
	useLocale();
	const router = useRouter();
	const acquirerData = S_ACQUIRER_POPUP.use([]);
	const [response, setResponse] = useState<IAPIResponse<any> | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	useEffect(() => {
		if (!acquirerData) return;
		setBusy(true);
		GD.REQ_API.request({
			method: "acquirers/get",
			data: { id: acquirerData.id },
		})
			.then(res => {
				setResponse(res);
			})
			.finally(() => {
				setBusy(false);
			});
	}, [acquirerData]);

	if (!acquirerData) return null;

	return (
		<ContentPopup
			selected={(acquirerData?.channelId ?? 0 > 0) ? 2 : undefined}
			busy={busy}
			error={response?.error}
			expandedWidth="1200px"
			closePopup={function (): void {
				S_ACQUIRER_POPUP.invoke(null);
			}}
			label={msg("provider_e0f3fde9") + (response?.data?.name ?? "")}
			extendedSectionID={2}>
			<TabItem section={1} title={msg("info")} icon={<TabIconInfo size={16} />}>
				<VBox sx={{ flexGrow: 1 }}>
					<AcquirerInfo acquirer={response?.data} />
					<Spacer grow />
					<HBox>
						<Spacer grow />
						<Button
							variant="secondarySmall"
							onClick={() => {
								S_ACQUIRER_POPUP.invoke(null);
								router.navigate("provider", { aid: response?.data.id + "" });
							}}>{msg("openProviderPage")}</Button>
					</HBox>
				</VBox>
			</TabItem>

			<TabItem section={2} title={msg("raw_ac0562bb")} icon={<TabIconFileJson size={16} />}>
				<Pre data={response?.data} />
			</TabItem>

			<TabItem section={3} title={msg("channels_19a39ad9")} icon={<TabIconNetwork size={16} />}>
				<AcquirerChannels key="fees" acquirer={response?.data} module="fees" />
			</TabItem>
		</ContentPopup>
	);
};

// ACQUIRER CHANNELS AND FEES COMPONENTS
const AcquirerChannels = ({ acquirer }: { acquirer: any | null; module?: string }) => {
	useLocale();
	const [selectedTerminal, setSelectedTerminal] = useState<ITerminal | null>(null);
	const [showDeleted, setShowDeleted] = useState<boolean>(false);

	if (!acquirer) return null;
	if (!acquirer.channels || acquirer.channels.length === 0) return <Error error={{ message: msg("noChannelsFoundForThisPayMethod") }} />;

	const terminalMap: { [key: number]: ITerminal } = {};
	acquirer.channels.forEach((channel: any) => {
		if (channel.terminal && channel.terminal.id) {
			terminalMap[channel.terminal.id] = channel.terminal;
		}
	});
	const terminals = Object.values(terminalMap);
	terminals.sort((a, b) => (a.id > b.id ? 1 : -1));

	// find channels for selected terminal
	let channels = acquirer.channels;
	if (selectedTerminal && selectedTerminal.id > 0)
		channels = acquirer.channels.filter((channel: any) => channel.terminal && channel.terminal.id === selectedTerminal?.id);

	if (!showDeleted) channels = channels.filter((channel: any) => channel.status !== "deleted");
	channels.sort((a: any, b: any) => (a.status > b.status ? 1 : -1));

	return (
		<TerminalsBlock
			all={true}
			merchantId={-1}
			scrollable={true}
			onChange={(terminal: ITerminal): void => {
				setSelectedTerminal(terminal);
			}}
			predefinedTerminals={[...terminals]}>
			<VBox gap="1px">
				{channels.map((channel: any, key: number) => {
					let opened = false;
					if (channels.length === 1) opened = true;
					return <AcquirerChannelItem key={key} channel={channel} open={opened} variant="acquirer" />;
				})}
			</VBox>

			<VBox
				sx={{
					position: "sticky",
					bottom: "-20px",
					//backgroundColor: "#f8f8fb",
					backdropFilter: "blur(20px)",
				}}>
				<Spacer size="20px" />
				<HBox>
					<Spacer grow />
					<Checkbox label={msg("showDeletedChannels")} name={"value"} data={{ value: showDeleted }} onChange={value => setShowDeleted(value)} />
				</HBox>
				<Spacer size="20px" />
			</VBox>
		</TerminalsBlock>
	);

	//acquirer.channels.map((channel: any, key: number) => <pre key={key}>{JSON.stringify(channel, null, 2)}</pre>);
};

export const AcquirerChannelItem = ({
	channel,
	open,
	variant,
	onClick,
}: {
	onClick?: (channel: any) => void;
	variant: "acquirer" | "newitem";
	channel: any;
	open?: boolean;
}) => {
	useLocale();
	const { alias, title, status, payout_status, terminal, channel_type } = channel;
	const [opened, setOpened] = useState(open);
	const [_formData, setFormData] = useState<any>(null); // fees
	const [_showAddFeeForm, _setShowAddFeeForm] = useState<boolean>(false);
	const [_formUpdater, setFormUpdater] = useState<{ func: () => void }>({ func: () => {} });

	let bgColor = opened ? "#1f3344" : "#ffffff";
	if (status === "deleted") bgColor = opened ? "#77274a" : "#faf3f3";

	let hoverBgColor = opened ? "#1f3344" : "#ecf3fa";
	if (status === "deleted") hoverBgColor = opened ? "#5e203b" : "#eae0e0";

	//backgroundColor: opened ? undefined : "#f0f0f0";

	return (
		<VBox>
			<HBox
				onClick={() => {
					//switch state
					if (onClick) onClick(channel);
					else setOpened(!opened);
				}}
				sx={{
					alignItems: "center",
					cursor: "pointer",
					padding: "10px",
					borderBottom: "1px solid rgba(0,0,0,0.1)",
					gap: "10px",
					transition: "background-color 0.2s",
					backgroundColor: bgColor,
					borderRadius: "4px",
					color: opened ? "#FFFFFF" : "inherit",
					"&:hover": { backgroundColor: hoverBgColor },
				}}>
				<TerminalBadge invert={opened} simple terminal={terminal} />

				<Typo variant="medium" color={opened ? "#FFFFFF" : "textPrimary"} bold>
					{title}
				</Typo>

				<Typo variant="medium" color={opened ? "#FFFFFF" : "textSecondary"}>
					{alias}
				</Typo>

				<Spacer grow />
				<Badge scheme={opened ? "dark" : "light"} variant="outline" label={msg("type_46f8246f") + channel_type} />
				<Badge scheme={opened ? "dark" : "light"} variant="outline" label={msg("payout_8e667a7d") + payout_status} />
				<Badge scheme={opened ? "dark" : "light"} variant="outline" label={msg("channel_9557d428") + status} />
				{variant === "acquirer" && (
					<Button
						variant="icon"
						onClick={() => {
							//switch state
							setOpened(!opened);
						}}>
						{opened ? <IconArrowDown scheme="dark" /> : <IconArrowDown sx={{ transform: "rotate(180deg)" }} />}
					</Button>
				)}
			</HBox>

			{opened && (
				<Box
					sx={{
						paddingBottom: "20px",
						position: "relative",
					}}>
					<Spacer size="20px" />
					<Tabs variant="round" spaceBottom={20}>
						<TabItem section={1} title={msg("fees_6d80486c")} icon={<TabIconPercent size={16} />}>
							<FeeParser
								channel={channel}
								requestFormUpdater={updater => {
									setFormUpdater({ func: updater });
								}}
								onDataLoaded={data => setFormData(data)}
							/>
						</TabItem>
						<TabItem section={3} title={msg("limits")} icon={<TabIconGauge size={16} />}>
							<Limits target_id={channel.id} target_type={"channel"} />
						</TabItem>
						<TabItem section={3} title={msg("info_170322a3")} icon={<TabIconInfo size={16} />}>
							<ChannelInfo channel={channel} />
						</TabItem>
						<TabItem section={2} title={msg("integrationSettings")} icon={<TabIconSettings2 size={16} />}>
							<SettingsParser key={"integration"} channel={channel} type="integration" />
						</TabItem>
					</Tabs>
				</Box>
			)}
		</VBox>
	);
};

const ChannelInfo = ({ channel }: { channel: any }) => {
	useLocale();
	// !TODO: busy state
	const [_busy, setBusy] = useState<boolean>(false);
	return (
		<VBox
			sx={{
				padding: "10px",
			}}>
			<FormParser
				sx={{ maxWidth: "auto" }}
				secondaryButton={null}
				buttons={[
					{
						title: msg("showMerchantsOnChannel"),
						variant: "secondarySmall",
						onClick: () => {},
					},
				]}
				key="acquirerChannelEditor"
				onSubmit={async data => {
					// submit
					setBusy(true);
					const result = await GD.REQ_API.request({
						method: "channels/add",
						data: data,
					});
					setBusy(false);
					return result;
				}}
				onRequest={async () => {
					setBusy(true);
					const result = await GD.REQ_API.request({
						method: "channels/get",
						data: {
							id: channel.id,
							acquirer_id: channel.acquirer_id,
						},
					});

					if (result.data) {
						result.data["$__decorator"] = {
							blocks: [
								/*{
									title: "Common Info",
									//	order: ["alias", "title"],
								},*/
								{
									title: msg("commonInfo_7c6977c3"),
									order: ["title", "alias"],
									gridTemplateColumns: ".7fr .3fr",
								},
								{
									title: msg("statuses"),
									order: ["status", "payout_status", "channel_type"],
									gridTemplateColumns: "1fr 1fr 1fr",
								},
							],
						};
					}

					setBusy(false);
					return result;
				}}
			/>
		</VBox>
	);
};

const findField = (data: any, fieldName: string): any => {
	if (!data) return null;
	if (Object.hasOwn(data, fieldName)) return data[fieldName];

	if (Array.isArray(data)) {
		for (const item of data) {
			if (typeof item === "object") {
				const res = findField(item, fieldName);
				if (res !== null) return res;
			}
		}
		return null;
	}

	const keys = Object.keys(data);
	for (const key of keys) {
		if (typeof data[key] === "object") {
			const res = findField(data[key], fieldName);
			if (res !== null) return res;
		}
	}
	return null;
};

// ACQUIRER INFO COMPONENT
const AcquirerInfo = ({ acquirer }: { acquirer: any | null }) => {
	useLocale();
	if (!acquirer) return null;

	const fields: { [key: string]: any } = [];
	const ignoreFields = ["project_id"];
	const order = ["id", "status", "name", "alias", "country", "timezone", "created_at", "updated_at"];
	for (const i of order) {
		if (ignoreFields.includes(i)) continue;
		let value = acquirer[i];
		if (typeof value === "object") continue;
		if (i === "status") {
			value = value.toUpperCase();
		}
		fields.push({ name: i, value: value });
	}

	return (
		<VBox>
			<HeadBlock>{msg("commonInfo_7c6977c3")}</HeadBlock>
			<GBox>
				{fields.map((field: any, key: number) => (
					<TitledValue key={key} variant="modern" title={field.name} value={field.value} />
				))}
			</GBox>
		</VBox>
	);
};

// SETTINGS PARSER
const SettingsParser = ({ channel, type }: { channel: any; type: string }) => {
	useLocale();
	return (
		<>
			<Spacer size="10px" />

			<FormParser
				sx={{ maxWidth: "auto" }}
				onSubmit={async (data: unknown): Promise<any> => {
					return GD.REQ_API.request({
						method: "settings/set",
						data: data,
					});
				}}
				onRequest={async (): Promise<unknown> => {
					return GD.REQ_API.request({
						method: "settings/get",
						data: {
							target_id: channel.id,
							target_type: "channel",
							type: type,
						},
					});
				}}
			/>
		</>
	);
};

// FEE PARSER
type TValues = {
	ic_pluss_pluss: boolean;
	fee: number;
	fee_fixed: number;
	starting_from: number;
	range: boolean;
	min_fee: number;
	max_fee: number;
};

type TRates = {
	type: string;
	values: TValues[];
};

type TFees = {
	currency: string;
	rates: TRates[];
};

type TEditValues = TValues & {
	selected_currency: string;
	selected_type: string;
	editable: boolean;
};

const FeeParser = ({
	channel,
	requestFormUpdater,
	onDataLoaded,
}: {
	channel: any;
	requestFormUpdater?: (updater: () => void) => void;
	onDataLoaded?: (data: any) => void;
}) => {
	useLocale();
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState<IError | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [_r, setRerender] = useState<number>(0);
	const [edit, setEdit] = useState<TEditValues | null | undefined>(undefined);
	const [saving, setSaving] = useState<boolean>(false);

	// rerender from outside
	useEffect(() => {
		if (requestFormUpdater) {
			requestFormUpdater(() => {
				setRerender(Date.now());
			});
		}
	}, []);

	// load data from server
	useEffect(() => {
		setError(null);
		setBusy(true);
		GD.REQ_API.request({
			method: "settings/get",
			data: {
				target_id: channel.id,
				target_type: "channel",
				type: "fees",
			},
		}).then(res => {
			setBusy(false);
			if (!res.error) {
				setData(res.data);
				if (onDataLoaded) onDataLoaded(res.data);
			} else setError(res.error);
		});
	}, [channel]);

	let currencyArray: TFees[] = [];

	if (data && data.data && data.data.currency && Array.isArray(data.data.currency)) {
		currencyArray = data.data.currency;
		currencyArray = currencyArray.filter((c: any) => c.currency !== "" && c.currency !== null);
	}

	let nofees = true;

	return (
		<Box>
			<ProgressBar busy={busy} />
			<Error error={error} />
			{saving && <Typo>{msg("saving")}</Typo>}
			<Spacer size="10px" />
			<VBox gap="20px">
				{currencyArray.map((currency: TFees, index) => {
					const key = index;

					const types = currency.rates.map((r: TRates, rIndex: number) => {
						const rKey = key + "_" + rIndex;
						// fees
						const fees = r.values.map((feeObj: TValues, fIndex: number) => {
							const fKey = rKey + "_" + fIndex;
							return (
								<HBox
									key={fKey}
									sx={{
										gap: "10px",
										border: "1px solid rgba(0,0,0,0.1)",
										borderRadius: "4px",
										alignItems: "center",
										padding: "10px",
										backgroundColor: "#fafdff",
									}}>
									<VBox gap="10px">
										<HBox variant="medium" sx={{ alignItems: "center", gap: "10px" }}>
											<IconCalendar />
											<Typo variant="medium">{msg("appliedFrom")}</Typo>
											<Typo variant="medium" bold>
												<DateTimeTooltip value={feeObj.starting_from}>{formatDisplayDate(feeObj.starting_from, { locale: "en-US", format: { weekday: "short", month: "short", day: "2-digit", year: "numeric" } })}</DateTimeTooltip>
											</Typo>
										</HBox>

										<HBox gap="10px" sx={{ alignItems: "center" }}>
											<FeeBlock label={msg("fee")} value={`${feeObj.fee}%`} />
											<FeeBlock label={msg("fixedFee")} value={parseFloat(feeObj.fee_fixed + "").toFixed(2) + " " + currency.currency} />
											{feeObj.range && (
												<>
													<FeeBlock label={msg("minFee")} value={parseFloat(feeObj.min_fee + "").toFixed(2) + " " + currency.currency} />
													<FeeBlock label={msg("maxFee")} value={parseFloat(feeObj.max_fee + "").toFixed(2) + " " + currency.currency} />
												</>
											)}
											{feeObj.ic_pluss_pluss && <FeeBlock label="IC++" value={feeObj.ic_pluss_pluss ? "Yes" : "No"} />}
										</HBox>
									</VBox>
									<Spacer grow />
									<Button
										variant="secondarySmall"
										title={msg("edit_262121c5")}
										onClick={() => {
											// get rates
											setEdit({
												...feeObj,
												selected_currency: currency.currency,
												selected_type: r.type,
												editable: true,
											});
										}}
									/>
									<Button
										variant="secondarySmall"
										title={msg("copy_6f5a6034")}
										onClick={() => {
											// get rates
											setEdit({
												...feeObj,
												selected_currency: currency.currency,
												selected_type: r.type,
												editable: false,
											});
										}}
									/>
								</HBox>
							);
						});

						let icon = null;
						if (r.type && r.type.toLowerCase().indexOf("visa") !== -1) icon = <IconVisa />;
						if (r.type && r.type.toLowerCase().indexOf("master") !== -1) icon = <IconMastercard />;
						if (r.type && r.type.toLowerCase() === "all") icon = <IconVisaAndMaster />;

						return (
							<VBox
								key={rIndex}
								sx={{
									backgroundColor: "#FFFFFF", //rgba(0,0,0,0.04)",
									borderRadius: "4px",
									padding: "10px",
									gap: "10px",
									boxShadow: "0 2px 1px rgba(0,0,0,0.1)",
								}}>
								<HBox
									sx={{
										alignItems: "center",
										gap: "10px",
									}}>
									{icon}
									{r.type}
								</HBox>
								{fees}
							</VBox>
						);
					});

					if (currency.rates.length === 1 && currency.rates[0].type === "") {
						return (
							<VBox
								key={key}
								sx={{
									padding: "20px",
									alignItems: "center",
									backgroundColor: "#ffffff",
									borderRadius: "4px",
									boxShadow: "0 2px 1px rgba(0,0,0,0.1)",
								}}>
								<Typo variant="medium" bold>{msg("noFeesDefinedYet")}</Typo>
								<Spacer size="10px" />
								<Button
									variant="secondarySmall"
									onClick={() => {
										const editblock = currency.rates[0].values[0];
										if (editblock) setEdit(JSON.parse(JSON.stringify(editblock)));
									}}
									title={msg("addFee")}
								/>
							</VBox>
						);
					}

					nofees = false;

					return (
						<VBox
							key={key}
							sx={{
								gap: "20px",
							}}>
							<Box>
								<VBox
									sx={{
										justifyContent: "center",
										boxShadow: "0 2px 1px rgba(0,0,0,0.1)",

										padding: "20px",
										borderRadius: "4px",
										backgroundColor: "#ffffff",
									}}>
									<Typo variant="medium" bold>{msg("feesFor")}{currency.currency}
									</Typo>
								</VBox>
								<Spacer size="10px" />
								<VBox
									sx={{
										gap: "10px",
										marginLeft: "20px",
									}}>
									{types}
								</VBox>
							</Box>
						</VBox>
					);
				})}
			</VBox>

			{edit !== undefined && (
				<Panel
					sx={{ minWidth: "420px" }}
					decorator="absolute"
					label={msg("addFeeRecord")}
					growContent
					busy={saving}
					onClose={() => {
						setEdit(undefined);
					}}>
					<Typo variant="text">{msg("addNewFeeRecordTo")}<br />
						{channel.title}
						<br />
						{channel.alias}
						<br />
						{channel.terminal.type}
						<br />
						{channel.status}
						<br />{msg("currencyTypeAndDateOfApplyingMustBeUnique")}</Typo>
					<Spacer size="20px" />

					<FormParser
						primaryButton={{
							title: edit?.editable ? msg("saveChanges") : msg("addNew"),
						}}
						secondaryButton={{ title: msg("cancel"), onClick: () => setEdit(undefined) }}
						size="small"
						onSubmit={async (req: any): Promise<any> => {
							const values = req.global;

							// data
							const cur = values.currency;
							const type = values.type;
							const from_date = values.from_date;
							const fees = values.fees;
							fees.starting_from = from_date;

							if (values.action === "add") {
								// check for duplicates
								let existedCurrency = (data.data.currency as TFees[]).find(c => c.currency === cur);
								if (!existedCurrency) {
									existedCurrency = {
										currency: cur,
										rates: [],
									};
									data.data.currency.push(existedCurrency);
								}

								// find rate type
								let existedRate = existedCurrency.rates.find(r => r.type === type);
								if (!existedRate) {
									existedRate = {
										type: type,
										values: [],
									};
									existedCurrency.rates.push(existedRate);
								}

								// check date uniqueness
								const existedValue = existedRate.values.find(v => v.starting_from === from_date);
								if (existedValue) {
									return {
										data: req,
										error: {
											message: msg("feeWithSuchCurrencyTypeAndDateOfApplyingAlreadyExists"),
										},
									};
								}

								// check if from_date more than starting_from in existing values
								for (const v of existedRate.values) {
									if (from_date < v.starting_from) {
										return {
											data: req,
											error: {
												message: msg("dateOfApplyingMustBeMoreThanInExistingRecords"),
											},
										};
									}
								}

								// add new value
								existedRate.values.push(fees);
							}

							// save to server
							setSaving(true);
							const response = await GD.REQ_API.request({
								method: "settings/set",
								data: data,
							});

							setSaving(false);

							if (response.error) {
								return {
									data: req,
									error: response.error,
								};
							}

							setEdit(undefined);
						}}
						onRequest={async (): Promise<any> => {
							const selected_currency = edit?.selected_currency ?? "";
							const selected_type = edit?.selected_type ?? "";
							const selected_from = (edit?.starting_from && edit.editable) ?? new Date().getTime();

							return {
								global: {
									action: edit?.editable ? "edit" : "add",
									from_date: selected_from,
									$__from_date_type: "date",
									$__from_date_disabled: edit?.editable ? true : false,

									currency: selected_currency,
									$__currency_values: [...(findField(data.data.currency[0], "$__currency_values") ?? [])],
									$__currency_type: "select",
									$__currency_disabled: edit?.editable ? true : false,

									type: selected_type,
									$__type_values: [...(findField(data.data.currency[0], "$__type_values") ?? [])],
									$__type_type: "select",
									$__type_disabled: edit?.editable ? true : false,

									fees: {
										...edit,
									},

									$__fees_decorator: {
										blocks: [
											{
												blockless: true,
												order: ["ic_pluss_pluss", "range"],
												gridTemplateColumns: "max-content max-content",
												sx: {
													alignItems: "center",
												},
											},
											{
												blockless: true,
												order: ["fee", "fee_fixed", "min_fee", "max_fee"],
												gridTemplateColumns: " 1fr 1fr",
												sx: {
													alignItems: "center",
												},
											},
										],
									},
								},

								$__global_decorator: {
									blocks: [
										{
											title: msg("currencyTypeAndDateOfApplying"),
											order: ["currency", "type", "from_date"],
											gridTemplateColumns: "100px 200px 1fr",
										},
										{
											title: msg("feesSettings"),
											order: ["fees"],
										},
									],
								},
							};
						}}
					/>
				</Panel>
			)}

			{!nofees && (
				<>
					<Spacer size="20px" />
					<HBox>
						<Spacer grow />
						<Button
							variant="secondarySmall"
							onClick={() => {
								const editblock = currencyArray.length > 0 ? currencyArray[0].rates[0].values[0] : null;
								if (editblock) setEdit(JSON.parse(JSON.stringify(editblock)));
								//todo: alert - no currency/rates defined
							}}
							title={msg("addFees")}
						/>
					</HBox>
				</>
			)}
		</Box>
	);
};

export const FeeBlock = ({ label, value, merchantValue }: { label: string; value: any; merchantValue?: any }) => {
	useLocale();
	let bgcolor = "#FFFFFF";
	let color = "textPrimary";
	if (value && (value + "").trim().toLowerCase() === "yes") {
		bgcolor = "rgb(0, 140, 255)";
		color = "#FFFFFF";
	}
	if (value && (value + "").trim().toLowerCase() === "no") bgcolor = "rgba(198, 198, 198, 0.6)";
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "row",
				minWidth: "80px",
				alignItems: "center",
				border: "1px solid rgba(0,0,0,0.1)",
				borderRadius: "4px",
				padding: "5px 10px",
				backgroundColor: bgcolor,
			}}>
			<Typo variant="caption" sx={{ color }}>
				{label}:
			</Typo>
			<Spacer size="10px" />
			<Typo variant="medium" sx={{ fontWeight: "500", color: color }}>
				{value}
			</Typo>
			{merchantValue !== undefined && (
				<>
					<Spacer size="5px" />
					<Typo variant="caption" sx={{ color }}>
						({merchantValue})
					</Typo>
				</>
			)}
		</Box>
	);
};
